/**
 * AccountDetail right-side panel: Company, Contacts, Status sections.
 *
 * Stays read-only for now — editing happens via dedicated buttons in each
 * section that open inline forms or modals (added in subsequent passes).
 */

import React from 'react';
import {Briefcase, Mail, Pencil, Phone, Plus, User as UserIcon} from 'lucide-react';
import type {LifecyclePhase, AccountsSubStage} from '@api/generated/client';
import {Button} from '@common-web/components/ui/button';
import {Card} from '@common-web/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@common-web/components/ui/select';
import {trpc} from '@common-web/lib/trpc';
import {useAuthStore} from '@common-web/stores/authStore';
import {CODE_PERMISSIONS} from '@common/permissions';
import {usePermissions} from '@common-web/hooks/use-permissions';
import {formatCurrency} from '@common/lib/utils';
import {AssignedBrokerLink} from '@common-web/components/account/AssignedBrokerLink';
import {EditContactModal, type EditableContact} from './EditContactModal';

const UNASSIGNED_BROKER = '__unassigned__';

interface AccountSidebarProps {
  accountsId: string;
  account: {
    companyName: string;
    lifecyclePhase: LifecyclePhase;
    subStage: AccountsSubStage;
    viewStageKey?: string | null;
    viewSubStageKey?: string | null;
    state: string | null;
    websiteUrl: string | null;
    estimatedFtEmployees: number | null;
    estimatedPtEmployees: number | null;
    estimatedStartDate: Date | string | null;
    averageEmployeeSalary: string | null;
    assignedBroker: {id: string; name: string} | null;
    group: {id: string; estimatedMemberCount: number | null} | null;
    contacts: Array<{
      id: string;
      firstName: string | null;
      lastName: string | null;
      email: string | null;
      phone: string | null;
      role: string | null;
      isPrimary: boolean;
    }>;
  };
  /** Read-only virtual VBA account — hides broker reassignment and contact editing. */
  readOnly?: boolean;
}

function formatDate(d: Date | string | null): string {
  if (!d) return '—';
  const date = typeof d === 'string' ? new Date(d) : d;
  return date.toLocaleDateString();
}

// Website URLs are entered free-form (no scheme required), so prefix a protocol
// when one is missing to keep the link absolute rather than relative.
function toHref(url: string): string {
  return /^https?:\/\//i.test(url) ? url : `https://${url}`;
}

function fullName(c: {firstName: string | null; lastName: string | null}): string {
  return [c.firstName, c.lastName].filter(Boolean).join(' ') || '—';
}

export function AccountSidebar({
  accountsId,
  account,
  readOnly = false,
}: AccountSidebarProps): React.ReactElement {
  const members = account.group?.estimatedMemberCount ?? account.estimatedFtEmployees;
  const primary = account.contacts.find((c) => c.isPrimary);
  const additional = account.contacts.filter((c) => !c.isPrimary);

  return (
    <aside className='w-80 shrink-0 overflow-y-auto border-l bg-muted/30'>
      <div className='space-y-4 p-4'>
        {/* Assigned Broker */}
        <AssignedBrokerCard
          accountsId={accountsId}
          assignedBroker={account.assignedBroker}
          readOnly={readOnly}
        />

        {/* Company */}
        <Card className='border-border p-4 shadow-sm'>
          <SectionTitle>Company</SectionTitle>
          <div className='mt-4 grid grid-cols-2 gap-x-4 gap-y-4'>
            <Field label='Name' value={account.companyName} className='col-span-2' />
            <Field label='State' value={account.state ?? '—'} />
            <Field label='Members' value={members !== null ? String(members) : '—'} />
            {account.estimatedPtEmployees !== null ? (
              <Field label='Part-time' value={String(account.estimatedPtEmployees)} />
            ) : null}
            {account.estimatedStartDate ? (
              <Field label='Est. start' value={formatDate(account.estimatedStartDate)} />
            ) : null}
            {account.averageEmployeeSalary ? (
              <Field
                label='Avg. salary'
                value={formatCurrency(account.averageEmployeeSalary, {
                  fallback: '—',
                  maximumFractionDigits: 0,
                })}
              />
            ) : null}
            {account.websiteUrl ? (
              <Field
                label='Website'
                className='col-span-2'
                value={
                  <a
                    href={toHref(account.websiteUrl)}
                    target='_blank'
                    rel='noreferrer'
                    className='inline-block max-w-full truncate font-semibold text-primary hover:underline'
                  >
                    {account.websiteUrl}
                  </a>
                }
              />
            ) : null}
          </div>
        </Card>

        {/* Contacts */}
        <ContactsCard
          accountsId={accountsId}
          primary={primary}
          additional={additional}
          readOnly={readOnly}
        />
      </div>
    </aside>
  );
}

function SectionTitle({children}: {children: React.ReactNode}): React.ReactElement {
  return (
    <h3 className='text-sm font-semibold uppercase tracking-wide text-foreground'>{children}</h3>
  );
}

/**
 * Displays the account's assigned broker, with inline editing for client and
 * internal admins. The broker list and the assignment itself are scoped on the
 * server (see accountsAccess.ts) — only admins can reassign, and the options
 * are limited to brokers within the tenant.
 */
function AssignedBrokerCard({
  accountsId,
  assignedBroker,
  readOnly = false,
}: {
  accountsId: string;
  assignedBroker: {id: string; name: string} | null;
  readOnly?: boolean;
}): React.ReactElement {
  const utils = trpc.useUtils();
  const user = useAuthStore((s) => s.user);
  const permissions = usePermissions();
  const canEdit =
    !readOnly &&
    ((!!user?.cbsInternalProfile && permissions.has(CODE_PERMISSIONS.tenant.accessAll.key)) ||
      (!!user?.clientProfile && permissions.has(CODE_PERMISSIONS.accounts.admin.key)));

  const [editing, setEditing] = React.useState(false);
  const [selected, setSelected] = React.useState(assignedBroker?.id ?? UNASSIGNED_BROKER);

  const {data: brokers} = trpc.account.listBrokers.useQuery({}, {enabled: editing && canEdit});

  const transfer = trpc.account.transferOwner.useMutation({
    onSuccess: () => {
      utils.account.getById.invalidate({accountsId});
      utils.account.getActivity.invalidate({accountsId});
      setEditing(false);
    },
  });

  function startEdit(): void {
    setSelected(assignedBroker?.id ?? UNASSIGNED_BROKER);
    setEditing(true);
  }

  function save(): void {
    transfer.mutate({
      accountsId,
      assignedBrokerId: selected === UNASSIGNED_BROKER ? null : selected,
    });
  }

  return (
    <Card className='p-4'>
      <div className='flex items-center justify-between'>
        <SectionTitle>Assigned Broker</SectionTitle>
        {canEdit && !editing ? (
          <Button variant='ghost' size='sm' className='-mr-2 h-6 px-2' onClick={startEdit}>
            <Pencil className='h-3 w-3' />
          </Button>
        ) : null}
      </div>

      {editing ? (
        <div className='mt-3 space-y-2'>
          <Select value={selected} onValueChange={setSelected}>
            <SelectTrigger>
              <SelectValue placeholder='Unassigned' />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={UNASSIGNED_BROKER}>Unassigned</SelectItem>
              {brokers?.map((broker) => (
                <SelectItem key={broker.id} value={broker.id}>
                  {broker.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {transfer.error ? (
            <div className='text-xs text-red-600'>{transfer.error.message}</div>
          ) : null}
          <div className='flex gap-2'>
            <Button size='sm' onClick={save} disabled={transfer.isPending}>
              {transfer.isPending ? 'Saving…' : 'Save'}
            </Button>
            <Button
              size='sm'
              variant='outline'
              onClick={() => setEditing(false)}
              disabled={transfer.isPending}
            >
              Cancel
            </Button>
          </div>
        </div>
      ) : (
        <div className='mt-3 flex items-center gap-2 text-sm'>
          <Briefcase className='h-4 w-4 shrink-0 text-muted-foreground' />
          <AssignedBrokerLink broker={assignedBroker} className='font-medium' />
        </div>
      )}
    </Card>
  );
}

/**
 * Primary + additional contacts. The pencil on each contact opens
 * EditContactModal (account.updateContact under the hood); editing an
 * additional contact also allows promoting it to primary. When the account has
 * no primary contact, an "Add contact" button opens the same modal in add mode.
 */
type ContactEditorState = {mode: 'add'} | {mode: 'edit'; contact: EditableContact} | null;

function ContactsCard({
  accountsId,
  primary,
  additional,
  readOnly = false,
}: {
  accountsId: string;
  primary: EditableContact | undefined;
  additional: EditableContact[];
  readOnly?: boolean;
}): React.ReactElement {
  const [editor, setEditor] = React.useState<ContactEditorState>(null);
  const openEdit = (contact: EditableContact): void => setEditor({mode: 'edit', contact});

  return (
    <Card className='border-border p-4 shadow-sm'>
      <div className='flex items-center justify-between'>
        <SectionTitle>Primary Contact</SectionTitle>
        {primary && !readOnly ? <EditContactButton contact={primary} onEdit={openEdit} /> : null}
      </div>
      <div className='mt-4'>
        {primary ? (
          <ContactCard contact={primary} />
        ) : (
          <div className='space-y-3'>
            <div className='text-sm text-muted-foreground'>No primary contact</div>
            {readOnly ? null : (
              <Button
                type='button'
                variant='outline'
                size='sm'
                onClick={() => setEditor({mode: 'add'})}
              >
                <Plus className='mr-1.5 h-3.5 w-3.5' />
                Add contact
              </Button>
            )}
          </div>
        )}
      </div>
      {additional.length > 0 ? (
        <div className='mt-4 space-y-4 border-t pt-4'>
          <SectionTitle>Additional Contacts</SectionTitle>
          <div className='space-y-4'>
            {additional.map((c) => (
              <div key={c.id} className='flex items-start gap-1'>
                <div className='min-w-0 flex-1'>
                  <ContactCard contact={c} />
                </div>
                {readOnly ? null : <EditContactButton contact={c} onEdit={openEdit} />}
              </div>
            ))}
          </div>
        </div>
      ) : null}

      <EditContactModal
        accountsId={accountsId}
        contact={editor?.mode === 'edit' ? editor.contact : null}
        open={editor !== null}
        onOpenChange={(open) => {
          if (!open) setEditor(null);
        }}
      />
    </Card>
  );
}

function EditContactButton({
  contact,
  onEdit,
}: {
  contact: EditableContact;
  onEdit: (contact: EditableContact) => void;
}): React.ReactElement {
  return (
    <Button
      variant='ghost'
      size='sm'
      className='-mr-2 h-6 shrink-0 px-2'
      onClick={() => onEdit(contact)}
      title='Edit contact'
    >
      <Pencil className='h-3 w-3' />
    </Button>
  );
}

function Field({
  label,
  value,
  className,
}: {
  label: string;
  value: React.ReactNode;
  className?: string;
}): React.ReactElement {
  return (
    <div className={className}>
      <dt className='text-xs font-medium text-muted-foreground'>{label}</dt>
      <dd className='mt-1 truncate text-sm font-semibold text-foreground'>{value}</dd>
    </div>
  );
}

function ContactCard({
  contact,
}: {
  contact: {
    firstName: string | null;
    lastName: string | null;
    email: string | null;
    phone: string | null;
    role: string | null;
  };
}): React.ReactElement {
  return (
    <div className='flex gap-3'>
      <div className='flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary'>
        <UserIcon className='h-4 w-4' />
      </div>
      <div className='min-w-0 flex-1 space-y-1'>
        <div className='truncate text-sm font-semibold text-foreground'>{fullName(contact)}</div>
        {contact.role ? <div className='text-xs text-muted-foreground'>{contact.role}</div> : null}
        {contact.email ? (
          <div className='flex items-center gap-1.5 text-xs'>
            <Mail className='h-3 w-3 shrink-0 text-muted-foreground' />
            <a href={`mailto:${contact.email}`} className='truncate text-primary hover:underline'>
              {contact.email}
            </a>
          </div>
        ) : null}
        {contact.phone ? (
          <div className='flex items-center gap-1.5 text-xs'>
            <Phone className='h-3 w-3 shrink-0 text-muted-foreground' />
            <a href={`tel:${contact.phone}`} className='text-primary hover:underline'>
              {contact.phone}
            </a>
          </div>
        ) : null}
      </div>
    </div>
  );
}
